import React, { useState } from "react";
import { SummaryDataApiResponse } from "../../../../models/summarydataModels/summaryModels";
import "./SummaruTable.css";
import { Tooltip } from "antd";

interface SummaruTableProps {
  data: SummaryDataApiResponse | {};
  isLoading: boolean;
}

const isSummaryDataApiResponse = (data: any): data is SummaryDataApiResponse => {
  return data && data.content && Array.isArray(data.content);
};

export const SummaruTable: React.FC<SummaruTableProps> = ({ data, isLoading }) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRowExpansion = (rowId: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('ru-RU').format(num);
  };

  const formatWeight = (kg: number | null | undefined): string => {
    if (kg === null || kg === undefined) return '-';
    if (kg >= 100) {
      return `${(kg / 100).toFixed(1)} ц`;
    }
    return `${kg} сом `;
  };

  const formatExportImport = (kg: number | null | undefined): string => {
    if (kg === null || kg === undefined || kg === 0) return '-';
    // Конвертируем кг в тонны (1000 кг = 1 т)
    if (kg >= 1000) {
      return `${(kg / 1000).toFixed(1)} т`;
    }
    return `${kg} кг`;
  };

  const calculateProgress = (completed: number, inProgress: number): number => {
    const total = completed + inProgress;
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const getTaskStatusClass = (statusId: number): string => {
    switch (statusId) {
      case 3: return 'completed';
      case 2: return 'in-progress';
      case 1: return 'not-required';
      default: return '';
    }
  };

  const renderTaskStatus = (statusName: string, statusId: number) => {
    const className = `task-status ${getTaskStatusClass(statusId)}`;
    return <span className={className}>{statusName}</span>;
  };

  // const renderProgressCircles = (
  //   completed: number,
  //   notCompleted: number,
  //   notRequired: number
  // ) => {
  //   return (
  //     <div className="progress-circles">
  //       <div className="circle-wrapper" title={`Выполнено: ${completed}`}>
  //         <div className="progress-circle1 completed">{completed}</div>
  //       </div>
  //       <div className="circle-wrapper" title={`Не выполнено: ${notCompleted}`}>
  //         <div className="progress-circle1 not-completed">{notCompleted}</div>
  //       </div>
  //       <div className="circle-wrapper" title={`Не требуется: ${notRequired}`}>
  //         <div className="progress-circle1 not-required">{notRequired}</div>
  //       </div>
  //     </div>
  //   );
  // };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!isSummaryDataApiResponse(data)) {
    return (
      <div className="no-data-container">
        Нет данных для отображения
      </div>
    );
  }

  const content = data.content || [];
  const totalElements = data.totalElements || 0;

  if (!content || content.length === 0) {
    return (
      <div className="no-data-container">
        Нет данных для отображения
      </div>
    );
  }

  const pageSize = data.size || 15;
  const currentPage = data.page || 1;
  const startIndex = (currentPage - 1) * pageSize + 1;

  return (
    <div className="summary-table-containersummaru">
      <table className="summary-table">
        <thead>
          <tr style={{ background: "#e0e0e0", borderRadius: "10px" }}>
            <th className="row-number">№</th>
            <th>
              Фермер
            </th>
            <th>
              Область
            </th>
            <th>
              Район
            </th>
            <th>
              Статусы задач
            </th>
            {/* <th>
              Выполнено
            </th> */}
            <th>
              Прогресс
            </th>
            <th>
              Культура
            </th>
            <th>
              Площадь (га)
            </th>
            <th>
              Получил (ц)
            </th>
            <th>
              Затратил (сом)
            </th>
            <th>
              Экспорт (т)
            </th>
            <th>
              Импорт (т)
            </th>

          </tr>
        </thead>
        <tbody>
          {content.map((item: any, index: number) => {
            const isExpanded = expandedRows.has(item.id);
            // const progress = calculateProgress(item.taskCompletedCount, item.taskInProgressCount);
            
            return (
              <React.Fragment key={item.id}>
                <tr>
                  <td className="row-number">{startIndex + index}</td>
                  <td>
                    <div className="farmer-cell">
                      <button
                        className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleRowExpansion(item.id)}
                      >
                        <svg viewBox="0 0 16 16" fill="currentColor">
                          <path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
                        </svg>
                      </button>
                      {item.farmerFullname}
                    </div>
                  </td>
                  <td>{item.regionalUnit.nameRu}</td>
                  <td>{item.areaUnit.nameRu}</td>
                  <td>
                      <Tooltip title={`Выполнено: ${item.taskCompletedCount}`}>
                        <span className='status-badge green'>{item.taskCompletedCount}</span>
                      </Tooltip>

                      <Tooltip title={`Не требуется: ${item.taskNotRequiredCount}`}>
                        <span className='status-badge yellow'>{item.taskNotRequiredCount}</span>
                      </Tooltip>

                      <Tooltip title={`Не выполнено: ${item.taskNotCompetedCount}`}>
                        <span className='status-badge red'>{item.taskNotCompetedCount}</span>
                      </Tooltip>
                    </td>

                  {/* <td>{item.taskCompletedCount}</td> */}
                  <td>
                    <span className="progress-badge">
                    {item.taskInProgressCount}%
                    </span>
                  </td>
                  <td>{item.crop.nameRu}</td>
                  <td>
                    <span className="area-value">{formatNumber(item.landArea)} га</span>
                  </td>
                  <td>
                    <span className="weight-value">{formatWeight(item.yieldInKg)}</span>
                  </td>
                  <td>
                    <span className="weight-value">{formatWeight(item.expensesAmountKgs)}</span>
                  </td>
                  <td>
                    <span className="weight-value">{formatExportImport(item.kgForExport)}</span>
                  </td>
                  <td>
                    <span className="weight-value">{formatExportImport(item.kgForImport)}</span>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="expanded-content">
                    <td colSpan={12}>
                      <div className="tasks-container">
                        <table className="tasks-table">
                          <thead>
                            <tr>
                              <th>ФИО Сотрудника</th>
                              <th>Тип задачи</th>
                              <th>Дата проведения</th>
                              <th>Кол-во урожая (ц)</th>
                              <th>Описание работы</th>
                              <th>Статус задачи</th>
                              <th>Экспорт (т)</th>
                              <th>Импорт (т)</th>
                              {/* <th>Результат работы</th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {item.tasks.map((task: any) => (
                              <tr key={task.id}>
                                <td>
                                  <span className="task-employee">{task.ruarFullname}</span>
                                </td>
                                <td>{task.workType.nameRu}</td>
                                <td>
                                  <span className="task-date">{task.dateOfWork}</span>
                                </td>
                                <td>
                                  <span className="task-yield">
                                    {task.yieldInKg ? formatWeight(task.yieldInKg) : '-'}
                                  </span>
                                </td>
                                <td>
                                  <span className="task-description">
                                    {task.description || '-'}
                                  </span>
                                </td>
                                <td>
                                  {renderTaskStatus(task.workStatus.nameRu, task.workStatus.id)}
                                </td>
                                {/* <td>
                                  <span className="task-result">
                                    {task.workStatus.id === 3 ? 'В работе' : 
                                     task.workStatus.id === 2 ? 'В работе' : 
                                     task.workStatus.id === 1 ? 'Не требуется' : '-'}
                                  </span>
                                </td> */}
                                <td>
                                <span className="task-description">
                                  {formatExportImport(task.kgForExport)}
                                </span>
                                </td>
                                <td>
                                  <span className="task-description">
                                  {formatExportImport(task.kgForImport)}
                                </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      
      <div className="pagination-container">
        <div className="pagination-info">
          Показано {startIndex}-{Math.min(startIndex + pageSize - 1, totalElements)} из {totalElements} записей
        </div>
      </div>
    </div>
  );
};

